<template>
  <form :class="$style.container" @submit.prevent="onSubmit">
    <div :class="$style.row">
      <elsa-v-datepicker
        label="Начало"
        v-model="from"
        :disabled="!season.is_editable_start"
        :clearable="false"
        :danger="!!errors.heat_date_start"
        :danger-text="errors.heat_date_start"
      />
    </div>
    <div :class="$style.row">
      <elsa-v-datepicker
        label="Окончание"
        v-model="to"
        :disabled="!season.is_editable_end"
        :clearable="false"
        :danger="!!errors.heat_date_end"
        :danger-text="errors.heat_date_end"
      />
    </div>

    <div :class="[$style.buildings, buildingsExpanded && $style.buildingsActive]">
      <div :class="$style.buildingsLabel" @click="buildingsExpanded=!buildingsExpanded">
        Продублировать начало отопительного сезона на другие дома
        <app-icon name="ChevronDown" size="12px" slot="icon" />
      </div>
      <div v-if="buildingsExpanded" :class="$style.buildingsTree">
        <div v-if="buildingsTreeError" :class="$style.error">{{ buildingsTreeError }}</div>
        <checkbox-tree
          v-else-if="buildingsTree"
          :tree="buildingsTree"
          v-model="buildings"
        />
        <div v-else class="block-loading" />
      </div>
      <div v-if="buildingsExpanded" :class="$style.buildingsSelected">Выбрано: {{ buildings.length }}</div>
    </div>


    <div :class="$style.footer">
      <vs-button button="submit" type="border">Сохранить</vs-button>
    </div>
  </form>
</template>

<script>
import formatDate        from "@/client/helpers/formatDate";
import CheckboxTree      from "@/components/CheckboxTree";
import parseDate         from "@/client/helpers/parseDate";
import notifyHttpError   from "@/mixins/notifyHttpError";
import modalContext      from "@/mixins/modalContext";
import mergeCommonErrors from "@/helpers/mergeCommonErrors";
import arrayToIdIndex    from "@/client/helpers/arrayToIdIndex";

export default {
  name:       "HeatSeasonEditPopup",
  components: { CheckboxTree },
  props:      ['season'],
  mixins:     [notifyHttpError, modalContext],
  data() {
    return {
      from:               parseDate(this.season.period_start_month),
      to:                 parseDate(this.season.period_end_month),
      buildings:          [this.season.buildings_id],
      buildingsTree:      null,
      buildingsTreeError: null,
      buildingsStateDict: {},
      buildingsExpanded:  false,
      errors:             {},
    };
  },
  computed: {
    groups() {
      return this.$store.getters['appUser/groupedHouses'];
    },
  },
  methods:  {
    loadBuildingsInfo() {
      this.buildingsTreeError = null;
      this.buildingsTree = null;
      this.$http.get(`/heat/buildings`, {
        params: {
          heat_date_start: formatDate(this.from, 'dd-MM-yyyy'),
          heat_date_end:   formatDate(this.to, 'dd-MM-yyyy'),
          mcs:             this.$store.getters['appUser/management_companies_id'],
          buildings_id:    this.season.buildings_id,
        },
      }).then(
        ( { data: { buildings } } ) => {
          this.buildingsStateDict = arrayToIdIndex(buildings);
          this.updateBuildings();
          this.errors = {};
        },
        ( error ) => {
          const { response: { data: { errors } } } = error;
          this.buildingsTreeError = 'Выберите корректные даты начала и окончания';
          if (errors) {
            this.errors = mergeCommonErrors(errors, ['heat_date_start', 'heat_date_end']);
          } else {
            this.notifyHttpError(error);
          }
        },
      );
    },

    updateBuildings() {
      const { groups } = this;
      this.buildingsTree = groups.map(group => ({
        title:      group.title,
        selectable: true,
        expanded:   true,
        children:   group.buildings.map(building => {
          const current = building.id === this.season.buildings_id;
          const { is_kv_shown, is_set_charges } = this.buildingsStateDict[building.id];
          let title = (
            <span>
              {building.title} {current ? '(уже выбрано)' : ''}
              {' '}
              {is_kv_shown && (
                <span class={this.$style.danger}>Квитанции уже показаны</span>
              )}
              {!is_kv_shown && is_set_charges && (
                <span class={this.$style.danger}>Начисления сгенерированы, не забудьте перегенерировать</span>
              )}
            </span>
          );
          return {
            title,
            value:      building.id,
            selectable: true,
            disabled:   current || is_kv_shown,
            children:   [],
          };
        }),
      }));
    },

    onSubmit() {
      this.$http.post(`heat/season`, {
        heat_date_start:        formatDate(this.from, 'dd-MM-yyyy'),
        heat_date_end:          formatDate(this.to, 'dd-MM-yyyy'),
        buildings_id:           this.season.buildings_id,
        duplicate_buildings_id: this.buildingsExpanded ? this.buildings.filter(id => id !== this.season.buildings_id) : [],
      }).then(
        () => {
          this.modalResolve();
          this.errors = {};
        },
        ( error ) => {
          const { response: { data: { errors } } } = error;
          if (errors) {
            this.errors = mergeCommonErrors(errors, ['heat_date_start', 'heat_date_end']);
          } else {
            this.notifyHttpError(error);
          }
        },
      );
    },
  },
  mounted() {
    this.loadBuildingsInfo();
  },

  watch: {
    from() {
      this.loadBuildingsInfo();
    },
    to() {
      this.loadBuildingsInfo();
    },
  },
};
</script>

<style lang="scss" module>
.container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.row {
  margin-bottom: 1em;
}

.buildings {
  border-top: 1px solid rgba(#000, .2);
  min-height: 0;
  padding-top: .5em;
  margin-top: 1em;
  margin-bottom: 1em;
  display: flex;
  flex-direction: column;
}

.buildingsActive {
  .buildingsLabel svg {
    transform: rotate(180deg);
  }
}

.buildingsLabel {
  font-weight: 500;
  margin-bottom: .5em;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.buildingsTree {
  height: 100%;
  overflow: auto;
  padding: 15px;
  flex: 1;
  min-height: 0;
  margin-bottom: .5em;
}

.buildingsSelected {
  font-weight: 500;
}

.footer {
  text-align: center;
}

.danger {
  font-size: .9em;
  color: color(danger);
}

.error {
  color: color(danger);
  margin: 0 -15px;
}
</style>
